import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { shopProducts } from '../../mocks/shop-products';

export default function ShopPage() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<string>('All');

  const categories = ['All', 'Apparel', 'Accessories', 'Drinkware'];

  const filteredProducts = selectedCategory === 'All' 
    ? shopProducts 
    : shopProducts.filter(product => product.category === selectedCategory);

  const getStockColor = (stock: string) => {
    if (stock === 'In stock') return 'text-emerald-600';
    if (stock === 'Low stock') return 'text-amber-600';
    return 'text-gray-400';
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="font-serif text-5xl md:text-6xl text-gray-900 mb-6">
            Merchandise
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Wear the RE:SET mindset.
          </p>
          <p className="text-lg text-gray-500 mt-4 max-w-xl mx-auto">
            Quiet essentials designed for everyday restoration.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  selectedCategory === category
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Product Grid */}
      <section className="pb-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                onClick={() => navigate(`/shop/${product.id}`)}
                className="group cursor-pointer"
              >
                <div className="relative aspect-[4/5] bg-gray-50 rounded-lg overflow-hidden mb-4">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4">
                    <span className={`text-xs font-medium ${getStockColor(product.stock)}`}>
                      {product.stock}
                    </span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="text-lg font-medium text-gray-900 group-hover:text-reset-sage transition-colors">
                      {product.name}
                    </h3>
                    <span className="text-lg font-medium text-gray-900 whitespace-nowrap">
                      £{product.price}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 leading-relaxed">
                    {product.description}
                  </p>
                  {product.variants.length > 1 && (
                    <p className="text-xs text-gray-400 mt-2">
                      Available in {product.variants.map(v => v.size).join(', ')}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
